import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, DollarSign, Users, Home, Utensils, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { boardingFeeApi, schoolYearApi } from '../lib/api';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
};

const boardingTypeConfig = {
  externe: { label: 'Externe', icon: Users, color: 'bg-slate-100 text-slate-700' },
  demi_pensionnaire: { label: 'Demi-pensionnaire', icon: Utensils, color: 'bg-amber-100 text-amber-700' },
  interne: { label: 'Interne', icon: Home, color: 'bg-emerald-100 text-emerald-700' },
};

export default function BoardingFeesPage() {
  const [fees, setFees] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    boarding_type: 'externe',
    name: '',
    monthly_fee: 0,
    registration_fee: 0,
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [feesRes, yearRes] = await Promise.all([
        boardingFeeApi.getAll({}),
        schoolYearApi.getActive()
      ]);
      setFees(feesRes.data || []);
      setSchoolYear(yearRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!schoolYear?.id) {
      toast.error('Aucune année scolaire active');
      return;
    }
    setFormLoading(true);
    try {
      const res = await boardingFeeApi.seedDefaults(schoolYear.id);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }
    setFormLoading(true);
    try {
      const data = { ...formData, school_year_id: schoolYear?.id || '' };
      if (editingFee) {
        await boardingFeeApi.update(editingFee.id, data);
        toast.success('Tarif modifié');
      } else {
        await boardingFeeApi.create(data);
        toast.success('Tarif créé');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      boarding_type: fee.boarding_type,
      name: fee.name,
      monthly_fee: fee.monthly_fee || 0,
      registration_fee: fee.registration_fee || 0,
      description: fee.description || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await boardingFeeApi.delete(id);
      toast.success('Tarif supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingFee(null);
    setFormData({
      boarding_type: 'externe',
      name: '',
      monthly_fee: 0,
      registration_fee: 0,
      description: '',
    });
  };

  const calculateTotal = (fee) => {
    return (fee.registration_fee || 0) + ((fee.monthly_fee || 0) * 12);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="boarding-fees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Tarifs des Régimes</h1>
          <p className="text-muted-foreground text-sm">
            Gérez les frais de scolarité par type d'hébergement
            {schoolYear && <span className="ml-2">• {schoolYear.name}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {fees.length === 0 && (
            <Button variant="outline" className="rounded-full" onClick={handleSeedDefaults} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer tarifs par défaut
            </Button>
          )}
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-fee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau tarif
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingFee ? 'Modifier le tarif' : 'Nouveau tarif'}</DialogTitle>
                <DialogDescription>Définir les frais pour un type d'hébergement</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type d'hébergement</Label>
                    <Select value={formData.boarding_type} onValueChange={(v) => setFormData({ ...formData, boarding_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="externe">Externe</SelectItem>
                        <SelectItem value="demi_pensionnaire">Demi-pensionnaire</SelectItem>
                        <SelectItem value="interne">Interne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nom du tarif *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Externe Standard" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frais d'inscription (FCFA)</Label>
                    <Input type="number" value={formData.registration_fee} onChange={(e) => setFormData({ ...formData, registration_fee: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Mensualité (FCFA)</Label>
                    <Input type="number" value={formData.monthly_fee} onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Description du tarif..." rows={2} />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
                  <Button type="submit" disabled={formLoading}>
                    {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingFee ? 'Modifier' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {fees.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2">Aucun tarif configuré</h3>
            <p className="text-muted-foreground text-sm mb-4">Créez les tarifs par défaut ou ajoutez-les manuellement</p>
            <Button onClick={handleSeedDefaults} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer tarifs par défaut
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {fees.map((fee) => {
            const config = boardingTypeConfig[fee.boarding_type] || boardingTypeConfig.externe;
            const Icon = config.icon;
            return (
              <Card key={fee.id} className="relative overflow-hidden" data-testid={`fee-card-${fee.boarding_type}`}>
                <div className={`absolute top-0 left-0 right-0 h-1 ${config.color.replace('text', 'bg').split(' ')[0]}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{fee.name}</CardTitle>
                        <Badge variant="outline" className="mt-1">{config.label}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(fee)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(fee.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {fee.description && (
                    <p className="text-sm text-muted-foreground">{fee.description}</p>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Inscription</span>
                      <span className="font-medium">{formatCurrency(fee.registration_fee || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mensualité</span>
                      <span className="font-medium">{formatCurrency(fee.monthly_fee || 0)}</span>
                    </div>
                  </div>
                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total annuel</span>
                      <span className="text-lg font-bold text-primary">{formatCurrency(calculateTotal(fee))}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
